import { LotteryActiveBatch } from 'src/types/Lottery';
import { formatRunMetric } from 'src/components/shared/RunProgress/runMetric';

export interface TrayMetric {
  loaded: number;
  loadable: number;
}

/**
 * The engine persists the applicant total only at the end of FETCHING
 * (`runner._step_fetching`), so until a batch reaches DISPOSITIONING its
 * `counts.loadable` is a placeholder zero and the tray would read a bare `0/0`.
 * The requested size is the honest upper bound for that window: it is what was
 * actually asked of HC2. A batch that has a real total contributes its real
 * loadable count, even when that count is legitimately zero.
 */
const APPLICANT_TOTAL_UNKNOWN_STATES = new Set([
  'REQUESTED',
  'PULLING',
  'FETCHING',
]);

function loadableDenominator(batch: LotteryActiveBatch): number {
  const loadable = batch.counts?.loadable ?? 0;
  if (APPLICANT_TOTAL_UNKNOWN_STATES.has(batch.state)) {
    return batch.requested_size ?? loadable;
  }
  return loadable;
}

/**
 * The tray's header number (spec 00 Q3): the loadable fraction, not a batch
 * count. Summed across every active batch so a user sees how much of the work
 * in flight has actually landed.
 */
export function computeTrayMetric(batches: LotteryActiveBatch[]): TrayMetric {
  return batches.reduce<TrayMetric>(
    (metric, batch) => ({
      loaded: metric.loaded + (batch.counts?.loaded ?? 0),
      loadable: metric.loadable + loadableDenominator(batch),
    }),
    { loaded: 0, loadable: 0 }
  );
}

export function formatTrayMetric(metric: TrayMetric): string {
  // Shared with the campaign tray (DD-031): a zero denominator renders nothing.
  return formatRunMetric({ done: metric.loaded, total: metric.loadable });
}
